// pages/Settings/EmailConnection.jsx
import React, { useState, useEffect } from "react";
import {
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Send,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const EmailConnection = () => {
  const { isDarkMode } = useTheme();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEmailConnectionStatus();
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching status:", error);
      setError("Failed to fetch email connection status");
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    try {
      setConnecting(true);
      setError("");
      setSuccess("");

      // Get auth URL from backend
      const response = await apiService.getEmailAuthUrl({ provider });
      const { authUrl } = response.data;

      console.log("Auth URL received:", authUrl); // Debug log

      if (!authUrl) {
        throw new Error("No auth URL received from server");
      }

      // Open OAuth window
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const authWindow = window.open(
        authUrl,
        "Connect Email",
        `width=${width},height=${height},left=${left},top=${top},popup=yes`,
      );

      if (!authWindow) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }

      // Poll for window close or message
      const pollTimer = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(pollTimer);
          setConnecting(false);
        }
      }, 500);

      // Listen for OAuth callback
      const handleMessage = async (event) => {
        // Verify origin for security
        const allowedOrigins = [
          window.location.origin,
          process.env.REACT_APP_API_URL,
        ];

        if (!allowedOrigins.some((origin) => event.origin.startsWith(origin))) {
          console.warn("Message from unknown origin:", event.origin);
          return;
        }

        console.log("Received message:", event.data); // Debug log

        if (event.data.type === "oauth-callback") {
          clearInterval(pollTimer);

          try {
            authWindow.close();
          } catch (e) {
            console.warn("Could not close auth window:", e);
          }

          window.removeEventListener("message", handleMessage);

          if (event.data.error) {
            setError(event.data.error);
            setConnecting(false);
            return;
          }

          try {
            await apiService.handleEmailCallback({
              code: event.data.code,
              state: event.data.state,
            });

            setSuccess("Email connected successfully!");
            await fetchStatus();
          } catch (error) {
            console.error("Callback error:", error);
            setError(
              error.response?.data?.message || "Failed to connect email",
            );
          } finally {
            setConnecting(false);
          }
        }
      };

      window.addEventListener("message", handleMessage);

      // Cleanup on component unmount
      return () => {
        clearInterval(pollTimer);
        window.removeEventListener("message", handleMessage);
      };
    } catch (error) {
      console.error("Error connecting email:", error);
      setError(error.message || "Failed to initiate email connection");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect your email?")) {
      return;
    }

    try {
      await apiService.disconnectEmail();
      setSuccess("Email disconnected successfully");
      await fetchStatus();
    } catch (error) {
      setError("Failed to disconnect email");
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setError("");
      await apiService.testConnection();
      setSuccess("Test email sent successfully! Check your inbox.");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div
          className={`w-8 h-8 border-2 ${isDarkMode ? "border-blue-600 border-t-transparent" : "border-blue-500 border-t-transparent"} rounded-full animate-spin`}
        />
      </div>
    );
  }

  return (
    <div
      className={`max-w-4xl mx-auto p-6 font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F]" : "bg-[#F4F5F7]"}`}
    >
      <div className="mb-6">
        <h1
          className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"} mb-2`}
        >
          Email Connection
        </h1>
        <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          Connect your email to send personalized emails from your CRM
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle
              className="text-red-400 flex-shrink-0 mt-0.5"
              size={20}
            />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle
              className="text-green-400 flex-shrink-0 mt-0.5"
              size={20}
            />
            <p className="text-sm text-green-300">{success}</p>
          </div>
        </div>
      )}

      {status?.connected ? (
        <div
          className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-xl border p-6`}
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center`}
              >
                <CheckCircle className="text-green-400" size={24} />
              </div>
              <div>
                <h3
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Email Connected
                </h3>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {status.email} ({status.provider})
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div
              className={`${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg p-4`}
            >
              <div
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
              >
                Provider
              </div>
              <div
                className={`${isDarkMode ? "text-white" : "text-gray-900"} font-medium capitalize`}
              >
                {status.provider}
              </div>
            </div>

            <div
              className={`${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg p-4`}
            >
              <div
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
              >
                Connected
              </div>
              <div
                className={`${isDarkMode ? "text-white" : "text-gray-900"} font-medium`}
              >
                {new Date(status.connectedAt).toLocaleDateString()}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {testing ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send size={18} />
                  <span>Send Test Email</span>
                </>
              )}
            </button>

            <button
              onClick={handleDisconnect}
              className="flex items-center gap-2 px-4 py-2 bg-red-600/10 border border-red-600/30 text-red-400 rounded-lg hover:bg-red-600/20 transition-colors"
            >
              <XCircle size={18} />
              <span>Disconnect</span>
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-xl border p-6`}
        >
          <div className="text-center mb-6">
            <div
              className={`w-16 h-16 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-full flex items-center justify-center mx-auto mb-4`}
            >
              <Mail
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                size={32}
              />
            </div>
            <h3
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"} mb-2`}
            >
              No Email Connected
            </h3>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Connect your email to start sending personalized emails
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => handleConnect("gmail")}
              disabled={connecting}
              className="flex items-center justify-center gap-3 p-4 bg-white hover:bg-gray-100 text-gray-900 rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              <Mail size={20} />
              <span>Connect Gmail</span>
              <ExternalLink size={16} />
            </button>

            <button
              onClick={() => handleConnect("outlook")}
              disabled={connecting}
              className="flex items-center justify-center gap-3 p-4 bg-[#0078D4] hover:bg-[#106EBE] text-white rounded-xl font-medium transition-colors disabled:opacity-50"
            >
              <Mail size={20} />
              <span>Connect Outlook</span>
              <ExternalLink size={16} />
            </button>
          </div>

          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <h4 className="text-sm font-medium text-blue-400 mb-2">
              Why connect your email?
            </h4>
            <ul
              className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"} space-y-1`}
            >
              <li>• Send emails directly from your email account</li>
              <li>• Emails appear in your sent folder</li>
              <li>• Track opens, clicks, and replies automatically</li>
              <li>• Use email templates with personalization</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailConnection;
